import { api } from '../api';
import { useResource } from './use-resource';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import type { SectionId } from './nav-items';
import type {
  AdminBusinessList,
  AdminCatalogItem,
  AdminClaimList,
  AdminOfferList,
  AdminRefund,
  BookingList,
  DisputeList,
  SubscriptionPaymentList,
} from '../mock/seeds/schema-types';

// Rail badges (design `NAV` counts, D:8428-8452): one live "needs a decision"
// count per section, read from the same status-filtered list operations the
// screens use — the contract has no dedicated count endpoint. Only sections the
// viewer can open are fetched (a finance admin never calls the approvals queue),
// and a failed read just shows no badge.
//
//   approvals  — businesses awaiting verification + services in
//                moderation + pending business claims (claims sit under
//                Approvals, DR-39) + business payment-KYC awaiting a
//                decision or the merchant (FR-A20)
//   bookings   — bookings in payment verification
//   offers     — offers awaiting review
//   disputes   — open disputes
//   payments   — pending refunds + subscription payments awaiting verification
//                (verification lives under Payments, DR-39)

export type RailBadges = Partial<Record<SectionId, number>>;

export const badgeLabel: Partial<Record<SectionId, MessageKey>> = {
  approvals: 'admin.shell.badge.approvals',
  bookings: 'admin.badge.bookings',
  offers: 'admin.shell.badge.offers',
  disputes: 'admin.badge.disputes',
  payments: 'admin.shell.badge.payments',
};

type Counter = (signal: AbortSignal) => Promise<number>;

const count = async (p: Promise<unknown>): Promise<number> => {
  try {
    const res = (await p) as { items?: unknown[] } | undefined;
    return res?.items?.length ?? 0;
  } catch {
    return 0;
  }
};

const COUNTERS: Partial<Record<SectionId, Counter>> = {
  approvals: async (signal) => {
    const [apps, services, claims, ...payKyc] = await Promise.all([
      // Every listing waits here for Business verification (DR-78; API-144 `?status`).
      count(api.GET('/admin/businesses', { signal, params: { query: { status: 'pending_approval' } } }) as Promise<AdminBusinessList>),
      count(api.GET('/admin/catalog-items', { signal, params: { query: { status: 'pending_review' } } }) as Promise<{ items: AdminCatalogItem[] }>),
      count(api.GET('/admin/claims', { signal, params: { query: { status: 'pending' } } }) as Promise<AdminClaimList>),
      // Payment KYC waiting on a decision or on the merchant (design payState review / more / nid).
      ...(['under_review', 'more_info', 'blocked_individual'] as const).map((kyc_state) =>
        count(api.GET('/admin/businesses', { signal, params: { query: { kyc_state } } }) as Promise<AdminBusinessList>),
      ),
    ]);
    return apps + services + claims + payKyc.reduce((a, b) => a + b, 0);
  },
  bookings: (signal) =>
    count(api.GET('/admin/bookings', { signal, params: { query: { status: 'payment_verification' } } }) as Promise<BookingList>),
  offers: (signal) =>
    count(api.GET('/admin/offers', { signal, params: { query: { status: 'pending_review' } } }) as Promise<AdminOfferList>),
  disputes: (signal) =>
    count(api.GET('/admin/disputes', { signal, params: { query: { status: 'open' } } }) as Promise<DisputeList>),
  payments: async (signal) => {
    const [refunds, subs] = await Promise.all([
      count(api.GET('/admin/refunds', { signal, params: { query: { status: 'pending' } } }) as Promise<{ items: AdminRefund[] }>),
      count(api.GET('/admin/subscription-payments', { signal, params: { query: { status: 'pending' } } }) as Promise<SubscriptionPaymentList>),
    ]);
    return refunds + subs;
  },
};

export function useRailBadges(sections: ReadonlySet<SectionId>): RailBadges {
  const key = [...sections].sort().join(',');
  const { data } = useResource<RailBadges>(async (signal) => {
    const entries = await Promise.all(
      (Object.keys(COUNTERS) as SectionId[])
        .filter((s) => sections.has(s))
        .map(async (s) => [s, await COUNTERS[s]!(signal)] as const),
    );
    return Object.fromEntries(entries) as RailBadges;
  }, [key]);
  return data ?? {};
}
